import type {
  RohsProductComplianceFileOutputDocument,
  RohsProductComplianceFilesState,
} from '@/shared/hooks/api/rohsComplianceFiles'
import { findFirstIncompleteRequiredStep } from '@/shared/briefing'
import { trackedRohsParts } from '@/v2/lib/trackedRohsParts'

export { findFirstIncompleteRequiredStep }

export type RohsBriefingStepKey =
  | 'applicability'
  | 'productDescription'
  | 'productIdentification'
  | 'manufacturerInformation'
  | 'productDrawings'
  | 'partEvidence'
  | 'euDeclaration'
  | 'productSignOff'

export type RohsBriefingStepStatus = 'done' | 'open' | 'partial' | 'guidance'

export type RohsBriefingStepRequiredness = 'required' | 'optional' | 'informational'

export type RohsBriefingStep = {
  key: RohsBriefingStepKey
  status: RohsBriefingStepStatus
  requiredness: RohsBriefingStepRequiredness
  completeCount?: number
  totalCount?: number
}

export type RohsLegislationBriefing = {
  steps: RohsBriefingStep[]
}

function findLatestCompletedOutputDocument(
  outputDocuments: RohsProductComplianceFileOutputDocument[]
): RohsProductComplianceFileOutputDocument | null {
  return (
    outputDocuments.find((document) => document.status === 'COMPLETED') ?? null
  )
}

export function rohsBriefingStepRequiredness(
  key: RohsBriefingStepKey
): RohsBriefingStepRequiredness {
  if (key === 'applicability') {
    return 'informational'
  }
  return 'required'
}

function partEvidenceStatus(
  completeCount: number,
  totalCount: number
): RohsBriefingStepStatus {
  if (totalCount === 0) {
    return 'open'
  }
  if (completeCount === totalCount) {
    return 'done'
  }
  if (completeCount === 0) {
    return 'open'
  }
  return 'partial'
}

function buildStep(
  key: RohsBriefingStepKey,
  status: RohsBriefingStepStatus,
  counts?: Pick<RohsBriefingStep, 'completeCount' | 'totalCount'>
): RohsBriefingStep {
  return {
    key,
    status,
    requiredness: rohsBriefingStepRequiredness(key),
    ...counts,
  }
}

export function buildRohsLegislationBriefing(input: {
  hasProductDescription: boolean
  hasProductIdentification: boolean
  hasManufacturerInformation: boolean
  hasProductDrawings: boolean
  files: RohsProductComplianceFilesState
}): RohsLegislationBriefing {
  const parts = trackedRohsParts(input.files.components)
  const completeParts = parts.filter((part) => part.completedAt != null)
  const hasEuDeclaration =
    findLatestCompletedOutputDocument(input.files.outputDocuments) != null

  let productDescriptionStatus: RohsBriefingStepStatus = 'open'
  if (input.hasProductDescription) {
    productDescriptionStatus = 'done'
  }

  let productIdentificationStatus: RohsBriefingStepStatus = 'open'
  if (input.hasProductIdentification) {
    productIdentificationStatus = 'done'
  }

  let manufacturerInformationStatus: RohsBriefingStepStatus = 'open'
  if (input.hasManufacturerInformation) {
    manufacturerInformationStatus = 'done'
  }

  let productDrawingsStatus: RohsBriefingStepStatus = 'open'
  if (input.hasProductDrawings) {
    productDrawingsStatus = 'done'
  }

  let euDeclarationStatus: RohsBriefingStepStatus = 'open'
  if (hasEuDeclaration) {
    euDeclarationStatus = 'done'
  }

  let productSignOffStatus: RohsBriefingStepStatus = 'open'
  if (input.files.product?.completedAt != null) {
    productSignOffStatus = 'done'
  }

  return {
    steps: [
      buildStep('applicability', 'done'),
      buildStep('productDescription', productDescriptionStatus),
      buildStep('productIdentification', productIdentificationStatus),
      buildStep('manufacturerInformation', manufacturerInformationStatus),
      buildStep('productDrawings', productDrawingsStatus),
      buildStep(
        'partEvidence',
        partEvidenceStatus(completeParts.length, parts.length),
        {
          completeCount: completeParts.length,
          totalCount: parts.length,
        }
      ),
      buildStep('euDeclaration', euDeclarationStatus),
      buildStep('productSignOff', productSignOffStatus),
    ],
  }
}
